import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import PeopleIcon from '@mui/icons-material/People';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Chip from '@mui/material/Chip';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import RelayService from './relayService';
import Button from '@mui/material/Button';
import CheckIcon from '@mui/icons-material/Check';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Tooltip from '@mui/material/Tooltip';
import RelayCreate from './relayCreate';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import { useNavigate } from 'react-router-dom';
import SettingsIcon from '@mui/icons-material/Settings';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FilterListIcon from '@mui/icons-material/FilterList';
import Badge from '@mui/material/Badge';
import useActivityStore from '../../stores/activityStore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const relayservice = new RelayService();

const RelayValidateButton = (props) => {
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [validated, setValidated] = useState(false);
  const [message, setMessage] = useState("");
  const [showToolTip, setShowToolTip] = useState(false);

  const validaterelay = () => {
    setLoading(true);
    relayservice.validateRelay(props.id).then((response) => {
      if (response.result === true) {
        setValidated(true);
      }
      else {
        setMessage(response.message);
        setShowToolTip(true);
        setTimeout(
          function () {
            setShowToolTip(false);
          }, 5000);
      }
      setChecked(true);
      setLoading(false);
    });
  }

  if (loading) { return (<Button variant="outlined" disabled><CircularProgress size="1rem" color="inherit" /></Button>); }
  else if (!loading && checked && validated) { return (<Button variant="outlined"><CheckIcon color="success" /></Button>); }
  else if (!loading && checked && !validated) { return (<Button variant="outlined"><Tooltip title={message} open={showToolTip}><ErrorOutlineIcon color="error" /></Tooltip></Button>); }
  else { return (<Button variant="contained" onClick={validaterelay} disabled={loading}>Test</Button>); }
}


const RelayRow = (props) => {
  const navigate = useNavigate();
  const [relay, setRelay] = useState([]);
  const [activating, setActivating] = useState(false);

  // Get real-time mount status from Zustand store
  const getMountConnectionCount = useActivityStore((state) => state.getMountConnectionCount);
  const isMountActive = useActivityStore((state) => state.isMountActive);

  useEffect(() => {
    setRelay(props.relay);
  }, [props.relay]);

  function toggle() {
    setActivating(true);
    if (relay.active) {
      relayservice.disableRelay(relay.id).then((response) => {
        if (response.result) {
          setRelay(prevState => ({
            ...prevState,
            active: false
          }));
          setActivating(false);
        }
      })
        .catch((error) => {
          setActivating(false);
        });
    }
    else {
      relayservice.enableRelay(relay.id).then((response) => {
        if (response.result) {
          setRelay(prevState => ({
            ...prevState,
            active: true
          }));
          setActivating(false);
        }
      })
        .catch((error) => {
          setActivating(false);
        });
    }
  }

  const isConnected = isMountActive(relay.name);
  const clientCount = getMountConnectionCount(relay.name);

  return (
    <TableRow
      key={relay.id}
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
    >
      <TableCell component="th" scope="row">
        <Chip label={relay.name} color="primary" />
      </TableCell>
      <TableCell component="th" scope="row">
        <Chip label={relay.dip ? "N/A" : relay.relay_mountpoint} color="info" variant="outlined" />
      </TableCell>
      <TableCell align="left">{relay.server}:{relay.port}</TableCell>
      <TableCell align="left">
        <Stack direction="row" spacing={2} alignItems="center">
          {activating ? (
            <CircularProgress size="1rem" color="inherit" />
          ) : (
            <Switch checked={relay.active} onClick={toggle} color="success" />
          )}
          {relay.active && (
            <Stack direction="row" spacing={1} alignItems="center">
              {isConnected ? (
                <>
                  <Chip label="Connected" color="success" size="small" icon={<FiberManualRecordIcon />} />
                  {clientCount > 0 && (
                    <Chip
                      label={`${clientCount} client${clientCount !== 1 ? 's' : ''}`}
                      color="info"
                      size="small"
                    />
                  )}
                </>
              ) : (
                <Chip label="Disconnected" color="error" size="small" variant="outlined" />
              )}
            </Stack>
          )}
        </Stack>
      </TableCell>
      <TableCell align="left">
      <Tooltip title="Edit">
        <IconButton onClick={() => navigate("/caster/relays/" + relay.id)}>
          <SettingsIcon />
        </IconButton>
      </Tooltip>
    </TableCell>
    </TableRow>
  );
}

export default function RelayList(props) {
  const [loading, setLoading] = useState(true);
  const [relays, setRelays] = useState([]);
  const [filteredRelays, setFilteredRelays] = useState([]);
  const [showCreateRelayForm, setShowCreateRelayForm] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchText, statusFilter, relays]);

  const applyFilters = () => {
    let filtered = [...relays];

    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(relay =>
        relay.name?.toLowerCase().includes(searchLower) ||
        relay.server?.toLowerCase().includes(searchLower) ||
        relay.relay_mountpoint?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(relay => relay.active === true);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(relay => relay.active === false);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      if (order === 'asc') {
        return aValue < bValue ? -1 : 1;
      } else {
        return aValue > bValue ? -1 : 1;
      }
    });

    setFilteredRelays(filtered);
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    applyFilters();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const refresh = () => {
    setLoading(true);
    relayservice.getRelays().then((response) => {
      setRelays(response);
      setShowCreateRelayForm(false);
      setLoading(false);
    });
  };

  const displayRelays = filteredRelays.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (relays.length === 0 && !loading) {
    return (
      <React.Fragment>
        <h1 className="page-title"><span className="fw-semi-bold">NTRIP Relays</span></h1>
        <Stack direction="column" alignItems="center">
          <Paper style={{ marginTop: "2em", marginBottom: "2em", padding: "1em", minWidth: "15vw", maxWidth: "50vw" }} >
            <h3>Add Relay</h3>
            <p>Relays allow this base to network multiple RTK stations together by connecting to other NTRIP casters over the internet as a client and serve their mountpoints as it's own. </p>
            <Stack spacing={2} direction="column" alignItems="center">
              <p>A valid login is required on the remote RTK station to relay it's mountpoints.</p>
              <p>This station must be able to communicate with the caster via the internet or local network.</p>
              <p>Use the form below to add a new relay station and verify connectivity.</p>
              <RelayCreate refresh={() => refresh()} />
            </Stack>
          </Paper>
        </Stack>
      </React.Fragment>
    );
  }
  else {
    return (
      <React.Fragment>
        <h1 className="page-title"><span className="fw-semi-bold">NTRIP Relays</span></h1>

        <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
          <TextField
            size="small"
            placeholder="Search relays..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ flexGrow: 1, maxWidth: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
              startAdornment={<FilterListIcon sx={{ ml: 1, mr: -0.5 }} />}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={refresh} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Chip
            label={`${filteredRelays.length} ${filteredRelays.length === 1 ? 'relay' : 'relays'}`}
            size="small"
          />
        </Stack>

        <Paper elevation={1}>
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="relays table">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'name'}
                      direction={orderBy === 'name' ? order : 'asc'}
                      onClick={() => handleRequestSort('name')}
                    >
                      Local Mount
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Relay Mount</TableCell>
                  <TableCell align="left">
                    <TableSortLabel
                      active={orderBy === 'server'}
                      direction={orderBy === 'server' ? order : 'asc'}
                      onClick={() => handleRequestSort('server')}
                    >
                      Server
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'active'}
                      direction={orderBy === 'active' ? order : 'asc'}
                      onClick={() => handleRequestSort('active')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayRelays.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      {searchText || statusFilter !== 'all' ? 'No relays found matching your filters' : 'No NTRIP relays'}
                    </TableCell>
                  </TableRow>
                ) : (
                  displayRelays.map((relay) => <RelayRow key={relay.id} relay={relay} />)
                )}
              </TableBody>
            </Table>
            {loading && <LinearProgress />}
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filteredRelays.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

        {showCreateRelayForm &&
          <Stack direction="column" alignItems="center">
            <Paper style={{ marginTop: "2em", marginBottom: "2em", padding: "1em", minWidth: "15vw", maxWidth: "50vw" }} >
              <h3>Add Relay</h3>
              <p>Relays allow this base to network multiple RTK stations together by connecting to other NTRIP casters over the internet as a client and serve their mountpoints as it's own. </p>
              <Stack spacing={2} direction="column" alignItems="center">
                <p>A valid login is required on the remote RTK station to relay it's mountpoints.</p>
                <p>This station must be able to communicate with the caster via the internet or local network.</p>
                <p>Use the form below to add a new relay station and verify connectivity.</p>
                <RelayCreate refresh={() => refresh()} />
              </Stack>
            </Paper>
          </Stack>}
        {relays.length > 0 && !showCreateRelayForm && <Button variant="contained" onClick={() => setShowCreateRelayForm(true)}>+ New Relay</Button>}
      </React.Fragment >
    );
  }
}
